import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Truck, ShieldCheck, Headphones, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const FALLBACK_BANNER = "https://images.unsplash.com/photo-1708011108852-fa4e33c9d8dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxtZW4lMjBneW0lMjBjbG90aGluZyUyMGRhcmt8ZW58MHx8fHwxNzkwODk3ODMzfDA&ixlib=rb-4.1.0&q=85";
const CAT_BG = {
  masculino: "https://images.unsplash.com/photo-1611672585731-fa10603fb9e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtZW4lMjBneW0lMjBjbG90aGluZyUyMGRhcmt8ZW58MHx8fHwxNzkwODk3ODMzfDA&ixlib=rb-4.1.0&q=85",
  feminino: "https://images.unsplash.com/photo-1606902965551-dce093cda6e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHx3b21lbiUyMGZpdG5lc3MlMjBhcHBhcmVsfGVufDB8fHx8MTc5MDg5NzgzM3ww&ixlib=rb-4.1.0&q=85",
  "roupas-de-academia": "https://images.unsplash.com/photo-1614231125961-38323d6c485b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwYWN0aXZld2VhciUyMGZhc2hpb258ZW58MHx8fHwxNzkwODk3ODMzfDA&ixlib=rb-4.1.0&q=85",
  promocoes: "https://images.unsplash.com/photo-1548690312-e3b507d8c110?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHx3b21lbiUyMGZpdG5lc3MlMjBhcHBhcmVsfGVufDB8fHx8MTc5MDg5NzgzM3ww&ixlib=rb-4.1.0&q=85",
  camisetas: "https://images.unsplash.com/photo-1628935291759-bbaf33a66dc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxtZW4lMjBneW0lMjBjbG90aGluZyUyMGRhcmt8ZW58MHx8fHwxNzkwODk3ODMzfDA&ixlib=rb-4.1.0&q=85",
};

export default function HomePage() {
  const [banners, setBanners] = useState([]);
  const [cats, setCats] = useState([]);
  const [novidades, setNovidades] = useState([]);
  const [maisVendidos, setMaisVendidos] = useState([]);

  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data || []));
    api.get("/categories").then((r) => setCats(r.data || []));
    api.get("/products", { params: { new_arrival: true, limit: 8 } }).then((r) => setNovidades(r.data || []));
    api.get("/products", { params: { best_seller: true, limit: 8 } }).then((r) => setMaisVendidos(r.data || []));
  }, []);

  const activeBanner = banners[0];
  const bgImage = activeBanner ? fileUrl(activeBanner.image_path) : FALLBACK_BANNER;
  const title = activeBanner?.title || "LANÇAMENTOS";
  const subtitle = activeBanner?.subtitle || "Coleção que acompanha o seu ritmo. Peças premium para treino, cidade e street.";
  const ctaLink = activeBanner?.cta_link || "/categoria/roupas-de-academia";
  const ctaLabel = activeBanner?.cta_label || "Comprar agora";

  return (
    <div className="pb-10">
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0">
          <img src={bgImage} alt="Hero" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 min-h-[82vh] flex items-center">
          <div className="max-w-2xl py-24">
            <div className="overline-label text-white">Nova coleção · 2026</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-tighter mt-6 leading-[0.95]">
              {title}
            </h1>
            <p className="text-zinc-300 text-base sm:text-lg mt-6 max-w-xl">{subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-10">
              <Button asChild className="bg-white text-black hover:bg-zinc-200 h-14 px-8 text-sm tracking-widest uppercase" data-testid="hero-cta-shop">
                <Link to={ctaLink}>{ctaLabel} <ArrowRight className="ml-2" size={16} /></Link>
              </Button>
              <Button asChild variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10 h-14 px-8 text-sm tracking-widest uppercase">
                <Link to="/categoria/promocoes">Ver promoções</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE / Benefits */}
      <section className="border-y border-white/10 bg-[#0A0A0C]">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Truck, label: "Frete grátis acima de R$ 299" },
            { icon: ShieldCheck, label: "Pagamento 100% seguro" },
            { icon: Sparkles, label: "Novidades toda semana" },
            { icon: Headphones, label: "Atendimento pelo WhatsApp" },
          ].map(({ icon: Icon, label }, i) => (
            <div key={i} className="flex items-center gap-3 text-sm text-zinc-300">
              <Icon size={20} className="text-white shrink-0" />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline-label">Explore</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter text-white mt-3">Categorias</h2>
          </div>
          <Link to="/categoria/masculino" className="text-sm text-zinc-400 hover:text-white hover-underline hidden md:inline">Ver todas →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cats.slice(0, 8).map((c, i) => {
            const bg = c.image_path ? fileUrl(c.image_path) : CAT_BG[c.slug] || CAT_BG.masculino;
            return (
              <Link
                key={c.id}
                to={`/categoria/${c.slug}`}
                className="group relative aspect-[3/4] overflow-hidden border border-white/10 fade-up"
                style={{ animationDelay: `${i * 50}ms` }}
                data-testid={`home-cat-${c.slug}`}
              >
                <img src={bg} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-display text-xl text-white tracking-tight">{c.name}</h3>
                  <span className="text-xs text-zinc-300 inline-flex items-center gap-1 mt-1 hover-underline">Explorar <ArrowRight size={12} /></span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* NOVIDADES */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline-label">Acabou de chegar</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter text-white mt-3">Novidades</h2>
          </div>
          <Link to="/categoria/masculino" className="text-sm text-zinc-400 hover:text-white hover-underline hidden md:inline">Ver tudo →</Link>
        </div>
        {novidades.length === 0 ? (
          <EmptyGrid label="Em breve novos produtos" />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {novidades.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>

      {/* MAIS VENDIDOS */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline-label">Preferidos do público</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter text-white mt-3">Mais vendidos</h2>
          </div>
        </div>
        {maisVendidos.length === 0 ? (
          <EmptyGrid label="Em breve os mais vendidos" />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {maisVendidos.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>

      {/* CTA band */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-10">
        <div className="relative border border-white/10 overflow-hidden p-10 md:p-14 bg-[#0A0A0C]">
          <div className="relative">
            <div className="overline-label">Promoções</div>
            <h3 className="font-display text-4xl md:text-5xl text-white tracking-tighter mt-3 max-w-2xl">Peças selecionadas com até 40% off</h3>
            <p className="text-zinc-400 mt-4 max-w-xl">Aproveite ofertas por tempo limitado. Estoque pode acabar a qualquer momento.</p>
            <Button asChild className="mt-8 bg-white text-black hover:bg-zinc-200 h-12 px-8 tracking-widest text-xs uppercase">
              <Link to="/categoria/promocoes">Ver ofertas</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function EmptyGrid({ label }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="aspect-[4/5] shimmer border border-white/5" />
      ))}
      <p className="col-span-full text-zinc-500 text-sm italic mt-4">{label}. Cadastre produtos pelo painel admin.</p>
    </div>
  );
}
